﻿package org.zq.pixelset

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.graphics.ImageBitmap
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch
import org.zq.pixelset.model.BatchProcessFailure
import org.zq.pixelset.model.ImageItem
import org.zq.pixelset.model.ImageSettings
import org.zq.pixelset.model.NormalizedRect
import org.zq.pixelset.model.OutputFormat
import org.zq.pixelset.model.PickedImage
import org.zq.pixelset.model.ProcessSettings
import org.zq.pixelset.model.ProcessedResult
import org.zq.pixelset.model.ProcessingMode
import org.zq.pixelset.model.RotationStep
import org.zq.pixelset.model.SavedAppSettings
import org.zq.pixelset.model.ScaleMode
import org.zq.pixelset.model.SizePreset
import org.zq.pixelset.platform.ImagePlatform
import org.zq.pixelset.platform.SettingsStore
import org.zq.pixelset.processing.ImageDecodeGuard
import org.zq.pixelset.processing.ImageResize
import org.zq.pixelset.processing.ImageTransforms
import org.zq.pixelset.processing.ThumbnailGenerator
import org.zq.pixelset.processing.ZipArchive

class PixelSetViewModel(private val scope: CoroutineScope) {
    private val imageItems = mutableStateListOf<ImageItem>()
    private val decodedCache = mutableMapOf<String, org.zq.pixelset.model.DecodedImage>()
    private var idCounter = 0
    private var autoPreviewJob: Job? = null
    private var batchJob: Job? = null
    private var persistJob: Job? = null

    val images: List<ImageItem> get() = imageItems

    var selectedId by mutableStateOf<String?>(null)
        private set

    val selectedItem: ImageItem?
        get() = imageItems.find { it.id == selectedId }

    val currentFile: PickedImage?
        get() = selectedItem?.picked

    val originalBitmap: ImageBitmap?
        get() = selectedItem?.originalBitmap

    val processedBitmap: ImageBitmap?
        get() = selectedItem?.processedBitmap

    val processedResult: ProcessedResult?
        get() = selectedItem?.processedResult

    val loadWarning: String?
        get() = selectedItem?.loadWarning

    val originalWidth: Int
        get() = selectedItem?.originalWidth ?: 0

    val originalHeight: Int
        get() = selectedItem?.originalHeight ?: 0

    var targetWidth by mutableIntStateOf(800)
    var targetHeight by mutableIntStateOf(600)
    var lockAspectRatio by mutableStateOf(true)
    var selectedPresetIndex by mutableIntStateOf(2)
    var outputFormat by mutableStateOf(OutputFormat.JPG)
    var quality by mutableFloatStateOf(0.85f)
    var scaleMode by mutableStateOf(ScaleMode.Fit)
    var processingMode by mutableStateOf(ProcessingMode.Resize)

    var rotationDegrees by mutableIntStateOf(0)
    var cropLeft by mutableFloatStateOf(0f)
    var cropTop by mutableFloatStateOf(0f)
    var cropRight by mutableFloatStateOf(0f)
    var cropBottom by mutableFloatStateOf(0f)
    var visualCrop by mutableStateOf(NormalizedRect())
    var useVisualCrop by mutableStateOf(false)
    var cropEditorEnabled by mutableStateOf(true)
    var brightness by mutableIntStateOf(0)
    var contrast by mutableFloatStateOf(1f)
    var saturation by mutableFloatStateOf(1f)

    var isProcessing by mutableStateOf(false)
        private set

    var isBatchCancellable by mutableStateOf(false)
        private set

    var batchProgress by mutableStateOf("")
        private set

    var autoPreviewProgress by mutableStateOf("")
        private set

    var errorMessage by mutableStateOf<String?>(null)
        private set

    val batchFailures = mutableStateListOf<BatchProcessFailure>()

    var comparisonPosition by mutableFloatStateOf(0.5f)

    var autoPreviewEnabled by mutableStateOf(false)

    val hasImages: Boolean get() = imageItems.isNotEmpty()
    val processedCount: Int get() = imageItems.count { it.processedResult != null }
    val isCompressMode: Boolean get() = processingMode == ProcessingMode.Compress
    val isConvertMode: Boolean get() = processingMode == ProcessingMode.Convert

    val rotationIndex: Int
        get() = RotationStep.entries.indexOf(RotationStep.fromDegrees(rotationDegrees))

    val processingModeIndex: Int
        get() = ProcessingMode.entries.indexOf(processingMode)

    init {
        SettingsStore.load()?.let { applySavedDefaults(it) }
    }

    fun pickAndLoadImages() {
        scope.launch {
            errorMessage = null
            try {
                val picked = ImagePlatform.pickImages()
                if (picked.isNotEmpty()) addImages(picked)
            } catch (e: Exception) {
                errorMessage = e.message ?: "加载图片失败"
            }
        }
    }

    fun onFilesDropped(files: List<PickedImage>) {
        if (files.isNotEmpty()) addImages(files)
    }

    fun onDropFailed() {
        errorMessage = "未能识别拖入的图片，请使用 JPG / PNG / WebP / GIF / BMP 文件"
    }

    fun addImages(pickedList: List<PickedImage>) {
        scope.launch {
            isProcessing = true
            errorMessage = null
            try {
                var firstNewId: String? = null
                for (picked in pickedList) {
                    val id = loadSingleImage(picked)
                    if (firstNewId == null) firstNewId = id
                }
                if (selectedId == null && firstNewId != null) {
                    selectImage(firstNewId)
                }
            } catch (e: Exception) {
                errorMessage = e.message ?: "加载图片失败"
            } finally {
                isProcessing = false
            }
        }
    }

    private suspend fun loadSingleImage(picked: PickedImage): String {
        val raw = ImagePlatform.decodeImage(picked.bytes)
        val (decoded, warning) = ImageDecodeGuard.normalize(raw)
        val id = "img_${idCounter++}"
        decodedCache[id] = decoded
        val thumbnailDecoded = ThumbnailGenerator.createThumbnail(decoded)
        val settings = buildDefaultImageSettings().copy(
            targetWidth = when (processingMode) {
                ProcessingMode.Resize -> targetWidth.coerceAtMost(decoded.width).coerceAtLeast(1)
                else -> decoded.width
            },
            targetHeight = when (processingMode) {
                ProcessingMode.Resize -> targetHeight.coerceAtMost(decoded.height).coerceAtLeast(1)
                else -> decoded.height
            },
        )
        val item = ImageItem(
            id = id,
            picked = picked,
            thumbnail = ImagePlatform.toImageBitmap(thumbnailDecoded),
            originalWidth = decoded.width,
            originalHeight = decoded.height,
            originalBitmap = ImagePlatform.toImageBitmap(decoded),
            settings = settings,
            loadWarning = warning,
        )
        imageItems.add(item)
        return id
    }

    fun selectImage(id: String) {
        commitSettingsToSelected()
        selectedId = id
        comparisonPosition = 0.5f
        imageItems.find { it.id == id }?.let { loadSettingsToUi(it.settings) }
    }

    fun removeImage(id: String) {
        commitSettingsToSelected()
        val index = imageItems.indexOfFirst { it.id == id }
        if (index < 0) return
        imageItems.removeAt(index)
        decodedCache.remove(id)
        if (selectedId == id) {
            val nextId = imageItems.getOrNull(index.coerceAtMost(imageItems.lastIndex))?.id
                ?: imageItems.lastOrNull()?.id
            if (nextId != null) selectImage(nextId) else selectedId = null
        }
    }

    fun clearAllImages() {
        cancelProcessing()
        imageItems.clear()
        decodedCache.clear()
        selectedId = null
        batchFailures.clear()
    }

    fun clearBatchFailures() {
        batchFailures.clear()
    }

    fun onAutoPreviewChanged(enabled: Boolean) {
        autoPreviewEnabled = enabled
        if (enabled) scheduleAutoPreview()
        else autoPreviewJob?.cancel()
        schedulePersistDefaults()
    }

    fun updateProcessingMode(index: Int) {
        processingMode = ProcessingMode.entries.getOrNull(index) ?: ProcessingMode.Resize
        notifySettingsChanged()
    }

    fun toggleLockAspectRatio() {
        lockAspectRatio = !lockAspectRatio
        notifySettingsChanged()
    }

    fun updateOutputFormat(format: OutputFormat) {
        outputFormat = format
        notifySettingsChanged()
    }

    fun updateQuality(value: Float) {
        quality = value
        notifySettingsChanged()
    }

    fun updateScaleMode(mode: ScaleMode) {
        scaleMode = mode
        notifySettingsChanged()
    }

    fun updateRotation(index: Int) {
        rotationDegrees = RotationStep.entries.getOrNull(index)?.degrees ?: 0
        notifySettingsChanged()
    }

    fun updateVisualCrop(rect: NormalizedRect) {
        val clamped = rect.clamped()
        visualCrop = clamped
        useVisualCrop = !clamped.isFullImage
        syncMarginsFromVisualCrop(clamped)
        notifySettingsChanged()
    }

    fun updateCropLeft(value: Float) {
        cropLeft = value.coerceIn(0f, 0.45f)
        syncVisualCropFromMargins()
        notifySettingsChanged()
    }

    fun updateCropTop(value: Float) {
        cropTop = value.coerceIn(0f, 0.45f)
        syncVisualCropFromMargins()
        notifySettingsChanged()
    }

    fun updateCropRight(value: Float) {
        cropRight = value.coerceIn(0f, 0.45f)
        syncVisualCropFromMargins()
        notifySettingsChanged()
    }

    fun updateCropBottom(value: Float) {
        cropBottom = value.coerceIn(0f, 0.45f)
        syncVisualCropFromMargins()
        notifySettingsChanged()
    }

    fun updateBrightness(value: Float) {
        brightness = value.toInt().coerceIn(-100, 100)
        notifySettingsChanged()
    }

    fun updateContrast(value: Float) {
        contrast = value.coerceIn(0.5f, 2f)
        notifySettingsChanged()
    }

    fun updateSaturation(value: Float) {
        saturation = value.coerceIn(0f, 2f)
        notifySettingsChanged()
    }

    fun resetAdvancedEdits() {
        rotationDegrees = 0
        cropLeft = 0f
        cropTop = 0f
        cropRight = 0f
        cropBottom = 0f
        visualCrop = NormalizedRect()
        useVisualCrop = false
        brightness = 0
        contrast = 1f
        saturation = 1f
        notifySettingsChanged()
    }

    fun onWidthChange(newWidth: Int) {
        val w = newWidth.coerceAtLeast(1)
        if (lockAspectRatio && originalWidth > 0 && originalHeight > 0) {
            val ratio = originalWidth.toFloat() / originalHeight
            targetWidth = w
            targetHeight = (w / ratio).toInt().coerceAtLeast(1)
        } else {
            targetWidth = w
        }
        syncPresetToCustom()
        notifySettingsChanged()
    }

    fun onHeightChange(newHeight: Int) {
        val h = newHeight.coerceAtLeast(1)
        if (lockAspectRatio && originalWidth > 0 && originalHeight > 0) {
            val ratio = originalWidth.toFloat() / originalHeight
            targetHeight = h
            targetWidth = (h * ratio).toInt().coerceAtLeast(1)
        } else {
            targetHeight = h
        }
        syncPresetToCustom()
        notifySettingsChanged()
    }

    fun onPresetSelected(index: Int) {
        selectedPresetIndex = index
        val preset = SizePreset.presets.getOrNull(index) ?: return
        if (preset.width > 0 && preset.height > 0) {
            targetWidth = preset.width
            targetHeight = preset.height
        }
        notifySettingsChanged()
    }

    private fun syncPresetToCustom() {
        selectedPresetIndex = 0
    }

    private fun syncMarginsFromVisualCrop(rect: NormalizedRect) {
        cropLeft = rect.left
        cropTop = rect.top
        cropRight = (1f - rect.right).coerceAtLeast(0f)
        cropBottom = (1f - rect.bottom).coerceAtLeast(0f)
    }

    private fun syncVisualCropFromMargins() {
        visualCrop = NormalizedRect.fromMargins(cropLeft, cropTop, cropRight, cropBottom)
        useVisualCrop = !visualCrop.isFullImage
    }

    private fun notifySettingsChanged() {
        if (autoPreviewEnabled) {
            commitSettingsToAll()
        } else {
            commitSettingsToSelected()
        }
        scheduleAutoPreview()
        schedulePersistDefaults()
    }

    private fun scheduleAutoPreview() {
        if (!autoPreviewEnabled || imageItems.isEmpty()) return
        autoPreviewJob?.cancel()
        autoPreviewJob = scope.launch {
            delay(AUTO_PREVIEW_DEBOUNCE_MS)
            if (!autoPreviewEnabled) return@launch
            runAutoPreviewBatch()
        }
    }

    private suspend fun runAutoPreviewBatch() {
        if (isProcessing) return
        commitSettingsToAll()
        val ids = imageItems.map { it.id }
        try {
            ids.forEachIndexed { index, id ->
                if (!autoPreviewEnabled || isProcessing) return
                if (ids.size > 1) autoPreviewProgress = "预览中 ${index + 1}/${ids.size}"
                processItemSync(id, recordBatchError = false)
            }
        } finally {
            autoPreviewProgress = ""
        }
    }

    fun cancelProcessing() {
        batchJob?.cancel()
        batchJob = null
        autoPreviewJob?.cancel()
        autoPreviewProgress = ""
        batchProgress = ""
        isProcessing = false
        isBatchCancellable = false
        imageItems.indices.forEach { index ->
            if (imageItems[index].isProcessing) {
                updateItem(index) { it.copy(isProcessing = false) }
            }
        }
    }

    fun processImage() {
        val id = selectedId ?: run {
            errorMessage = "请先选择图片"
            return
        }
        commitSettingsToSelected()
        processSingleItem(id)
    }

    fun processAllImages() {
        if (imageItems.isEmpty()) {
            errorMessage = "请先上传图片"
            return
        }
        commitSettingsToAll()
        autoPreviewJob?.cancel()
        batchJob?.cancel()
        batchFailures.clear()
        batchJob = scope.launch(Dispatchers.Default) {
            isProcessing = true
            isBatchCancellable = true
            errorMessage = null
            try {
                val ids = imageItems.map { it.id }
                ids.forEachIndexed { index, id ->
                    ensureActive()
                    batchProgress = "处理中 ${index + 1}/${ids.size}"
                    processItemSync(id, recordBatchError = true)
                }
            } catch (_: CancellationException) {
                errorMessage = "批量处理已取消"
            } finally {
                batchProgress = ""
                isProcessing = false
                isBatchCancellable = false
                batchJob = null
            }
        }
    }

    private fun processSingleItem(id: String) {
        autoPreviewJob?.cancel()
        batchJob?.cancel()
        batchJob = scope.launch(Dispatchers.Default) {
            isProcessing = true
            isBatchCancellable = false
            errorMessage = null
            try {
                processItemSync(id, recordBatchError = false)
            } finally {
                isProcessing = false
                batchJob = null
            }
        }
    }

    private suspend fun processItemSync(id: String, recordBatchError: Boolean) {
        val index = imageItems.indexOfFirst { it.id == id }
        if (index < 0) return
        val item = imageItems[index]
        val settings = item.settings
        val source = decodedCache[id] ?: run {
            updateItem(index) {
                it.copy(isProcessing = false, lastProcessError = "图片数据已失效，请重新上传")
            }
            return
        }
        updateItem(index) { it.copy(isProcessing = true, lastProcessError = null) }
        try {
            val output = when (settings.processingMode) {
                ProcessingMode.Convert -> {
                    if (settings.outputFormat == OutputFormat.JPG) {
                        flattenAlpha(source)
                    } else {
                        source
                    }
                }
                ProcessingMode.Compress -> ImageTransforms.applyEdits(source, settings.toEditParams())
                ProcessingMode.Resize -> ImageResize.resize(
                    ImageTransforms.applyEdits(source, settings.toEditParams()),
                    settings.targetWidth.coerceAtLeast(1),
                    settings.targetHeight.coerceAtLeast(1),
                    settings.scaleMode,
                )
            }
            val bytes = ImagePlatform.encodeImage(output, settings.outputFormat, settings.quality)
            val bitmap = ImagePlatform.toImageBitmap(output)
            val result = ProcessedResult(bytes, output.width, output.height, bitmap)
            updateItem(index) {
                it.copy(
                    processedResult = result,
                    processedBitmap = bitmap,
                    isProcessing = false,
                    lastProcessError = null,
                )
            }
        } catch (e: Exception) {
            val msg = e.message ?: "处理失败"
            updateItem(index) { it.copy(isProcessing = false, lastProcessError = msg) }
            if (e !is CancellationException) {
                if (recordBatchError) {
                    batchFailures.add(BatchProcessFailure(item.picked.name, msg))
                } else {
                    errorMessage = "${item.picked.name}: $msg"
                }
            }
        }
    }

    private fun updateItem(index: Int, transform: (ImageItem) -> ImageItem) {
        imageItems[index] = transform(imageItems[index])
    }

    fun downloadProcessed() {
        val item = selectedItem ?: run {
            errorMessage = "请先选择图片"
            return
        }
        val result = item.processedResult ?: run {
            errorMessage = "请先处理图片"
            return
        }
        ImagePlatform.downloadFile(result.bytes, processedFileName(item))
    }

    fun downloadAllProcessed() {
        val files = imageItems.mapNotNull { item ->
            item.processedResult?.let { result ->
                processedFileName(item) to result.bytes
            }
        }
        if (files.isEmpty()) {
            errorMessage = "没有已处理的图片可下载"
            return
        }
        if (files.size == 1) {
            ImagePlatform.downloadFile(files.first().second, files.first().first)
        } else {
            val zipBytes = ZipArchive.create(files)
            ImagePlatform.downloadFile(zipBytes, "pixelset_images.zip")
        }
    }

    fun currentSettings(): ProcessSettings = ProcessSettings(
        targetWidth = targetWidth,
        targetHeight = targetHeight,
        lockAspectRatio = lockAspectRatio,
        format = outputFormat,
        quality = quality,
        scaleMode = scaleMode,
    )

    private fun commitSettingsToSelected() {
        val id = selectedId ?: return
        val index = imageItems.indexOfFirst { it.id == id }
        if (index < 0) return
        imageItems[index] = imageItems[index].copy(settings = buildImageSettingsFromUi())
    }

    private fun commitSettingsToAll() {
        val settings = buildImageSettingsFromUi()
        for (i in imageItems.indices) {
            imageItems[i] = imageItems[i].copy(settings = settings)
        }
    }

    private fun processedFileName(item: ImageItem): String {
        val baseName = item.picked.name.substringBeforeLast('.').ifEmpty { "image" }
        val ext = item.settings.outputFormat.extension
        val suffix = if (item.settings.processingMode == ProcessingMode.Convert) "converted" else "processed"
        return "${baseName}_$suffix.$ext"
    }

    private fun buildImageSettingsFromUi(): ImageSettings = ImageSettings(
        targetWidth = targetWidth,
        targetHeight = targetHeight,
        lockAspectRatio = lockAspectRatio,
        selectedPresetIndex = selectedPresetIndex,
        outputFormat = outputFormat,
        quality = quality,
        scaleMode = scaleMode,
        processingMode = processingMode,
        rotationDegrees = rotationDegrees,
        cropLeft = cropLeft,
        cropTop = cropTop,
        cropRight = cropRight,
        cropBottom = cropBottom,
        visualCrop = visualCrop,
        useVisualCrop = useVisualCrop,
        brightness = brightness,
        contrast = contrast,
        saturation = saturation,
    )

    private fun buildDefaultImageSettings(): ImageSettings = buildImageSettingsFromUi()

    private fun loadSettingsToUi(settings: ImageSettings) {
        targetWidth = settings.targetWidth
        targetHeight = settings.targetHeight
        lockAspectRatio = settings.lockAspectRatio
        selectedPresetIndex = settings.selectedPresetIndex
        outputFormat = settings.outputFormat
        quality = settings.quality
        scaleMode = settings.scaleMode
        processingMode = settings.processingMode
        rotationDegrees = settings.rotationDegrees
        cropLeft = settings.cropLeft
        cropTop = settings.cropTop
        cropRight = settings.cropRight
        cropBottom = settings.cropBottom
        visualCrop = settings.visualCrop
        useVisualCrop = settings.useVisualCrop
        brightness = settings.brightness
        contrast = settings.contrast
        saturation = settings.saturation
    }

    private fun buildSavedDefaults(): SavedAppSettings = SavedAppSettings(
        targetWidth = targetWidth,
        targetHeight = targetHeight,
        lockAspectRatio = lockAspectRatio,
        selectedPresetIndex = selectedPresetIndex,
        outputFormat = outputFormat.name,
        quality = quality,
        scaleMode = scaleMode.name,
        processingMode = processingMode.name,
        autoPreviewEnabled = autoPreviewEnabled,
        rotationDegrees = rotationDegrees,
        cropLeft = cropLeft,
        cropTop = cropTop,
        cropRight = cropRight,
        cropBottom = cropBottom,
        brightness = brightness,
        contrast = contrast,
        saturation = saturation,
    )

    private fun applySavedDefaults(saved: SavedAppSettings) {
        targetWidth = saved.targetWidth
        targetHeight = saved.targetHeight
        lockAspectRatio = saved.lockAspectRatio
        selectedPresetIndex = saved.selectedPresetIndex
        outputFormat = runCatching { OutputFormat.valueOf(saved.outputFormat) }.getOrDefault(OutputFormat.JPG)
        quality = saved.quality
        scaleMode = runCatching { ScaleMode.valueOf(saved.scaleMode) }.getOrDefault(ScaleMode.Fit)
        processingMode = runCatching { ProcessingMode.valueOf(saved.processingMode) }.getOrDefault(ProcessingMode.Resize)
        autoPreviewEnabled = saved.autoPreviewEnabled
        rotationDegrees = saved.rotationDegrees
        cropLeft = saved.cropLeft
        cropTop = saved.cropTop
        cropRight = saved.cropRight
        cropBottom = saved.cropBottom
        brightness = saved.brightness
        contrast = saved.contrast
        saturation = saved.saturation
        visualCrop = NormalizedRect.fromMargins(cropLeft, cropTop, cropRight, cropBottom)
        useVisualCrop = !visualCrop.isFullImage
    }

    private fun schedulePersistDefaults() {
        persistJob?.cancel()
        persistJob = scope.launch {
            delay(PERSIST_DEBOUNCE_MS)
            SettingsStore.save(buildSavedDefaults())
        }
    }

    private fun flattenAlpha(image: org.zq.pixelset.model.DecodedImage): org.zq.pixelset.model.DecodedImage {
        val out = IntArray(image.pixels.size)
        for (i in image.pixels.indices) {
            val pixel = image.pixels[i]
            val a = (pixel ushr 24) and 0xFF
            if (a == 255) {
                out[i] = pixel
            } else {
                val r = (pixel shr 16) and 0xFF
                val g = (pixel shr 8) and 0xFF
                val b = pixel and 0xFF
                val blendR = (r * a + 255 * (255 - a)) / 255
                val blendG = (g * a + 255 * (255 - a)) / 255
                val blendB = (b * a + 255 * (255 - a)) / 255
                out[i] = (0xFF shl 24) or (blendR shl 16) or (blendG shl 8) or blendB
            }
        }
        return org.zq.pixelset.model.DecodedImage(image.width, image.height, out)
    }

    companion object {
        private const val AUTO_PREVIEW_DEBOUNCE_MS = 400L
        private const val PERSIST_DEBOUNCE_MS = 500L
    }
}
